import { inject, Injectable } from "@angular/core";
import { EFileType } from "../interfaces/file-type.enum";
import { getFileType } from "../helpers/file-helpers";
import { ImagePreviewService } from "./image-preview.service";
import { PdfPreviewService } from "./pdf-preview.service";
import { FileModel } from "../helpers/file-model";
import { ImagePreviewConfig } from "../interfaces/image-preview.interfaces";
import { PdfPreviewConfig } from "../interfaces/pdf-preview.interfaces";

export interface FilePreviewConfig {
  file: FileModel | string;
  imageConfig?: Omit<ImagePreviewConfig, "file">;
  pdfConfig?: Omit<PdfPreviewConfig, "file">;
}

/**
 * Unified file preview coordinator.
 *
 * Routes to the appropriate preview service based on file type:
 * - Images -> {@link ImagePreviewService}
 * - PDFs   -> {@link PdfPreviewService}
 *
 * **Not root-provided.** Register via `provideFilePreview()` or provide manually.
 */
@Injectable()
export class FilePreviewService {
  private readonly imagePreview = inject(ImagePreviewService);
  private readonly pdfPreview = inject(PdfPreviewService);

  /**
   * Opens a preview for the given file.
   * @returns `true` if a preview was opened, `false` if the file type is not previewable.
   */
  open(config: FilePreviewConfig): boolean {
    const { file, imageConfig, pdfConfig } = config;

    if (this.resolveFileType(file) === EFileType.IMAGE) {
      this.imagePreview.open({ file, ...imageConfig });
      return true;
    }

    if (this.isPdf(file)) {
      const key = file instanceof FileModel ? file.key : file;
      this.pdfPreview.open({ file: key ?? null, ...pdfConfig });
      return true;
    }

    return false;
  }

  canPreview(file: FileModel | string): boolean {
    return this.resolveFileType(file) === EFileType.IMAGE || this.isPdf(file);
  }

  private resolveFileType(file: FileModel | string): EFileType {
    if (file instanceof FileModel) return file.type ?? EFileType.UNKNOWN;
    return getFileType(file.split(".").pop() ?? "");
  }

  private isPdf(file: FileModel | string): boolean {
    const ext = file instanceof FileModel ? file.extension : (file.split(".").pop() ?? "");
    return ext.toLowerCase() === "pdf";
  }
}
